import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { removeToken, getSuperAdminData } from "../utils/tokenUtils";
import logo from '../assets/image/elaba-transparent.png';

const Sidebar = ({ isOpen, toggleSidebar, isCollapsed = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const superAdmin = getSuperAdminData();

  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  const confirmLogout = () => {
    removeToken();
    setShowLogoutConfirm(false);
    // Small timeout to ensure state updates before navigation
    setTimeout(() => {
      navigate("/login");
    }, 50);
  };

  const cancelLogout = () => {
    setShowLogoutConfirm(false);
  };

  const renderIcon = (iconType, active = false) => {
    const iconStyle = {
      width: '20px',
      height: '20px',
      stroke: active ? '#ffffff' : 'rgba(255, 255, 255, 0.85)',
      fill: 'none',
      strokeWidth: active ? '1.8' : '1.5',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
      filter: active ? 'drop-shadow(0 0 6px rgba(255,255,255,0.25))' : 'none'
    };

    switch (iconType) {
      case 'dashboard':
        return (
          <svg viewBox="0 0 24 24" style={iconStyle}>
            <rect x="3" y="3" width="7" height="7"/>
            <rect x="14" y="3" width="7" height="7"/>
            <rect x="14" y="14" width="7" height="7"/>
            <rect x="3" y="14" width="7" height="7"/>
          </svg>
        );
      case 'shops':
        return (
          <svg viewBox="0 0 24 24" style={iconStyle}>
            <path d="M3 21h18"/>
            <path d="M5 21V7l8-4v18"/>
            <path d="M19 21V11l-6-4"/>
          </svg>
        );
      case 'users':
        return (
          <svg viewBox="0 0 24 24" style={iconStyle}>
            <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
            <circle cx="9" cy="7" r="4"/>
            <path d="M22 21v-2a4 4 0 0 0-3-3.87"/>
            <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
          </svg>
        );
      case 'bookings':
        return (
          <svg viewBox="0 0 24 24" style={iconStyle}>
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
            <line x1="16" y1="2" x2="16" y2="6"/>
            <line x1="8" y1="2" x2="8" y2="6"/>
            <line x1="3" y1="10" x2="21" y2="10"/>
          </svg>
        );
      case 'services':
        return (
          <svg viewBox="0 0 24 24" style={iconStyle}>
            <path d="M12 1v3"/>
            <path d="M12 20v3"/>
            <path d="M4.22 4.22l2.12 2.12"/>
            <path d="M17.66 17.66l2.12 2.12"/>
            <path d="M1 12h3"/>
            <path d="M20 12h3"/>
            <circle cx="12" cy="12" r="5"/>
          </svg>
        );
      case 'revenue':
        return (
          <svg viewBox="0 0 24 24" style={iconStyle}>
            <line x1="12" y1="1" x2="12" y2="23"/>
            <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
          </svg>
        );
      case 'analytics':
        return (
          <svg viewBox="0 0 24 24" style={iconStyle}>
            <line x1="18" y1="20" x2="18" y2="10"/>
            <line x1="12" y1="20" x2="12" y2="4"/>
            <line x1="6" y1="20" x2="6" y2="14"/>
          </svg>
        );
      case 'messages':
        return (
          <svg viewBox="0 0 24 24" style={iconStyle}>
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
          </svg>
        );
      case 'notifications':
        return (
          <svg viewBox="0 0 24 24" style={iconStyle}>
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
            <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
          </svg>
        );
      case 'settings':
        return (
          <svg viewBox="0 0 24 24" style={iconStyle}>
            <circle cx="12" cy="12" r="3"/>
            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1 1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
          </svg>
        );
      default:
        return <div style={{ width: '18px', height: '18px' }} />;
    }
  };

  // Menu definition
  const menuItems = [
    {
      path: "/dashboard",
      iconType: "dashboard",
      label: "Dashboard"
    },
    {
      path: "/shops",
      iconType: "shops",
      label: "Laundry Shops"
    },
    {
      path: "/bookings",
      iconType: "bookings",
      label: "Bookings"
    },
    {
      path: "/services",
      iconType: "services",
      label: "Services"
    },
    {
      path: "/admins",
      iconType: "users",
      label: "Shop Admins"
    },
    {
      path: "/users",
      iconType: "users",
      label: "Customers"
    },
    // Temporarily disabled (not yet available): Revenue → Settings
    // Keep definitions here for future re-enable
    // Revenue removed (confidential)
    {
      path: "/reports",
      iconType: "analytics",
      label: "Analytics"
    },
    {
      path: "/messages",
      iconType: "messages",
      label: "Messages"
    },
    {
      path: "/notifications",
      iconType: "notifications",
      label: "Notifications"
    },
    {
      path: "/settings",
      iconType: "settings",
      label: "Settings"
    }
  ];

  // Hide unimplemented items (Revenue → Settings) without deleting their configs
  const DISABLE_UNIMPLEMENTED = true;
  const TEMP_DISABLED_PATHS = [
    "/messages",
    "/notifications",
  ];
  const visibleMenuItems = DISABLE_UNIMPLEMENTED
    ? menuItems.filter(mi => !TEMP_DISABLED_PATHS.includes(mi.path))
    : menuItems;

  return (
    <>
      {/* Overlay for mobile */}
      {isOpen && (
        <div 
          className="sidebar-overlay"
          onClick={toggleSidebar}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 999,
            display: window.innerWidth <= 768 ? 'block' : 'none'
          }}
        />
      )}

      {/* Sidebar */}
      <div 
        className={`sidebar ${isOpen ? 'open' : ''}`}
        style={{
          position: 'fixed',
          top: 0,
          left: window.innerWidth <= 768 ? (isOpen ? 0 : '-260px') : (isOpen ? 0 : '-260px'),
          width: window.innerWidth <= 768 ? (isOpen ? '260px' : '0px') : (isCollapsed && window.innerWidth > 768 ? '72px' : '260px'),
          height: '100vh',
          background: 'linear-gradient(135deg, #23272f 0%, #2d3748 100%)',
          color: 'white',
          transition: 'left 0.3s cubic-bezier(.4,2,.6,1), width 0.3s cubic-bezier(.4,2,.6,1)',
          zIndex: window.innerWidth <= 768 ? 1200 : 900,
          overflowY: 'auto',
          boxShadow: window.innerWidth <= 768 ? '0 0 0 rgba(0,0,0,0)' : (isCollapsed && window.innerWidth > 768 ? 'none' : '6px 0 32px 0 #283e5111'),
          borderRight: window.innerWidth <= 768 ? 'none' : (isCollapsed && window.innerWidth > 768 ? 'none' : '1.5px solid #2d374822'),
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          opacity: window.innerWidth <= 768 && !isOpen ? 0 : 1,
          pointerEvents: window.innerWidth <= 768 && !isOpen ? 'none' : 'auto',
        }}
      >
        {/* Logo/Header */}
        <div style={{
          padding: isCollapsed ? '20px 10px 16px' : '32px 20px 18px 20px',
          borderBottom: 'none',
          display: 'flex',
          alignItems: 'center',
          justifyContent: isCollapsed ? 'center' : 'flex-start',
          gap: '12px',
        }}>
          
          <div style={{
            width: 48,
            height: 48,
            borderRadius: '14px',
            background: 'linear-gradient(135deg, #2d3748 0%, #23272f 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 2px 8px #23272f33',
            overflow: 'hidden',
          }}>
            <img 
              src={logo} 
              alt="eLaba Logo" 
              style={{ width: 50, height: 50, objectFit: 'contain', display: 'block', borderRadius: 10 }} 
            />
          </div>
          {!isCollapsed && <h2 style={{ 
            margin: 0, 
            fontSize: '22px', 
            fontWeight: '700',
            color: 'white',
            letterSpacing: '0.5px',
            textShadow: '0 2px 8px #3b82f622'
          }}>
            eLaba Admin
          </h2>}
        </div>

        {/* User Info Section */}
        {superAdmin && !isCollapsed && (
          <div style={{
            padding: '16px 20px',
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
            marginBottom: '8px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <div style={{
                width: '36px',
                height: '36px',
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.2) 0%, rgba(255, 255, 255, 0.1) 100%)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginRight: '12px',
                fontSize: '16px',
                color: 'white',
                fontWeight: '600',
                border: '1px solid rgba(255, 255, 255, 0.2)'
              }}>
                {superAdmin.username.charAt(0).toUpperCase()}
              </div>
              <div>
                <p style={{ 
                  margin: 0, 
                  fontSize: '15px', 
                  fontWeight: '500',
                  color: 'white'
                }}>
                  {superAdmin.username}
                </p>
                <p style={{ 
                  margin: '2px 0 0 0', 
                  fontSize: '13px', 
                  color: 'rgba(255, 255, 255, 0.7)'
                }}>
                  Super Administrator
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Navigation Menu */}
        <nav style={{ padding: isCollapsed ? '8px 4px' : '8px 0', marginBottom: '90px' }}>
          {visibleMenuItems.map((item) => (
            <div
              key={item.path}
              onClick={() => {
                if (item.path === location.pathname) {
                  window.location.reload();
                } else {
                  navigate(item.path);
                  if (window.innerWidth <= 768) {
                    // auto-close sidebar on mobile after navigation
                    toggleSidebar();
                  }
                }
              }}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  if (item.path === location.pathname) {
                    window.location.reload();
                  } else {
                    navigate(item.path);
                    if (window.innerWidth <= 768) toggleSidebar();
                  }
                }
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: isCollapsed ? 'center' : 'flex-start',
                padding: isCollapsed ? '12px 0' : '14px 24px',
                cursor: 'pointer',
                background: location.pathname === item.path
                  ? 'rgba(255,255,255,0.10)'
                  : 'transparent',
                borderRadius: '8px',
                margin: isCollapsed ? '0 4px 6px' : '0 10px 6px 10px',
                boxShadow: location.pathname === item.path ? '0 1.5px 6px #23272f22' : 'none',
                transition: 'all 0.18s cubic-bezier(.4,2,.6,1)',
                position: 'relative',
                border: location.pathname === item.path ? '1.5px solid #4a5568' : '1.5px solid transparent',
                backdropFilter: location.pathname === item.path ? 'blur(2px)' : 'none',
                outline: 'none',
                title: isCollapsed ? item.label : undefined
              }}
              onMouseEnter={(e) => {
                if (location.pathname !== item.path) {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.08)';
                  e.currentTarget.style.border = '1.5px solid #e0e7ef33';
                }
              }}
              onMouseLeave={(e) => {
                if (location.pathname !== item.path) {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.border = '1.5px solid transparent';
                }
              }}
              aria-current={location.pathname === item.path ? 'page' : undefined}
            >
              {location.pathname === item.path && (
                <div
                  style={{
                    position: 'absolute',
                    left: 0,
                    top: 6,
                    bottom: 6,
                    width: 3,
                    borderRadius: 2,
                    background: 'linear-gradient(180deg, #667eea 0%, #764ba2 100%)',
                    boxShadow: '0 0 12px rgba(118,75,162,0.35)'
                  }}
                />
              )}
              <div style={{ 
                marginRight: isCollapsed ? 0 : '14px',
                width: isCollapsed ? '24px' : '20px',
                minWidth: isCollapsed ? '24px' : '20px',
                height: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                filter: 'none',
                opacity: location.pathname === item.path ? 1 : 0.85,
                flexShrink: 0,
              }}>
                {renderIcon(item.iconType, location.pathname === item.path)}
              </div>
              {!isCollapsed && <span style={{ 
                fontSize: '16px', 
                fontWeight: location.pathname === item.path ? '600' : '400',
                color: location.pathname === item.path ? '#fff' : 'rgba(255,255,255,0.92)',
                letterSpacing: '0.01em',
                textShadow: location.pathname === item.path ? '0 1.5px 4px #23272f22' : 'none',
              }}>
                {item.label}
              </span>}
            </div>
          ))}
        </nav>

      </div>

      {/* Logout Confirmation Modal - Moved outside sidebar */}
      {showLogoutConfirm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: 'rgba(0,0,0,0.55)',
          zIndex: 10000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <div style={{
            background: '#fff',
            borderRadius: '14px',
            padding: '32px 24px 24px 24px',
            boxShadow: '0 8px 48px rgba(0,0,0,0.25)',
            minWidth: '320px',
            maxWidth: '90vw',
            textAlign: 'center',
            transform: 'scale(1)',
            animation: 'fadeIn 0.2s ease-out',
          }}>
            <h3 style={{ 
              color: '#d32f2f', 
              marginBottom: '18px', 
              fontWeight: 700,
              fontSize: '20px' 
            }}>
              Confirm Logout
            </h3>
            <p style={{ 
              color: '#333', 
              marginBottom: '28px', 
              fontSize: '16px',
              lineHeight: '1.4' 
            }}>
              Are you sure you want to logout?
            </p>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '18px' }}>
              <button
                onClick={confirmLogout}
                style={{
                  background: 'linear-gradient(90deg, #ef4444 0%, #dc2626 100%)',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '7px',
                  fontWeight: 600,
                  fontSize: '15px',
                  cursor: 'pointer',
                  boxShadow: '0 2px 8px #ef444433',
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-1px)';
                  e.target.style.boxShadow = '0 4px 12px #ef444466';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 2px 8px #ef444433';
                }}
              >
                Yes, Logout
              </button>
              <button
                onClick={cancelLogout}
                style={{
                  background: '#f3f4f6',
                  color: '#333',
                  border: '1px solid #e5e7eb',
                  padding: '12px 24px',
                  borderRadius: '7px',
                  fontWeight: 500,
                  fontSize: '15px',
                  cursor: 'pointer',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = '#e5e7eb';
                  e.target.style.transform = 'translateY(-1px)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = '#f3f4f6';
                  e.target.style.transform = 'translateY(0)';
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

    </>
  );
};

export default Sidebar;